// src/components/Supplier/HoldJobsDetails.js
import React, { useState, useEffect } from "react";
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Pagination,
    Stack,
    Paper,
    TextField,
} from "@mui/material";
import ScaleLoader from "react-spinners/ScaleLoader";
import noTableData from "../../assets/images/noTableData.svg";
import API from "../../utils/Axios.utils";
import useStyles from "../../assets/styles/SupMangComp.style";
import useDebounce from "../../utils/useDebounce";
import useAuth from "../../hooks/useAuth";

const HoldJobsDetails = ({ handleDialog }) => {
    const classes = useStyles();
    const [showLoader, setShowLoader] = useState(false);

    const [searchText, setSearchText] = useState("");
    const debouncedSearchText = useDebounce(searchText, 500);
    const [jobs, setJobs] = useState([]);
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(1);

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    const { token } = useAuth();
    const config = {
        headers: { Authorization: `Bearer ${token}` },
    };

    const getHoldJobs = async () => {
        setShowLoader(true);
        let url = `/api/dash/holdjoblist?page=${page}&pageSize=10`;
        if (debouncedSearchText.trim()) {
            url += `&search=${encodeURIComponent(debouncedSearchText.trim())}`;
        }

        try {
            const res = await API.get(url, config);
            const data = res?.data || {};
            // BE function: res.json({ jobs, page, pageSize, totalCount })
            setJobs(res?.data?.jobs || []);
            setListCount(data.totalCount || 0);
        } catch (error) {
            console.log("error", error);
            setJobs([]);
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        getHoldJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearchText]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "-";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    return (
        <div>
            <Grid container xs={12}>
                <Grid
                    container
                    xs={12}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item>
                        <span className={classes.headingText}>On-hold Jobs</span>
                    </Grid>
                    <Grid item>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by Job Code / Title"
                            value={searchText}
                            onChange={(e) => {
                                setPage(1);
                                setSearchText(e.target.value);
                            }}
                            style={{ minWidth: 260 }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid container xs={12} className={classes.tableGrid}>
                {showLoader ? (
                    <div
                        style={{
                            display: "flex",
                            height: 400,
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
                    </div>
                ) : jobs.length === 0 ? (
                    <div
                        style={{
                            height: 300,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                        }}
                    >
                        <img src={noTableData} width={60} alt="No Data" />
                        <div
                            style={{
                                marginTop: 5,
                                fontWeight: "500",
                                fontSize: 25,
                                fontFamily: "'Noto Sans', sans-serif",
                            }}
                        >
                            There are no on-hold jobs to show.
                        </div>
                    </div>
                ) : (
                    <TableContainer component={Paper}>
                        <Table
                            sx={{ minWidth: 650 }}
                            size="small"
                            aria-label="on-hold jobs table"
                        >
                            <TableHead>
                                <TableRow className={classes.tableHeadRow}>
                                    <TableCell className={classes.tableHeadCell}>
                                        Job Code
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Job Title
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Core Skills
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Created Date
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody className={classes.noHorizontalBorderTableRow}>
                                {jobs.map((row) => (
                                    <TableRow key={row.job_id}>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.job_code || "-"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.title || "-"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.jobCoreSkill?.name ||
                                                row.jobCoreSkill?.core_skill_name ||
                                                "-"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {formatDate(row.createdAt || row.posted_on_date)}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.job_status || "On Hold"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Stack
                            spacing={2}
                            sx={{
                                padding: 2,
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "end",
                            }}
                        >
                            <Pagination
                                classes={{ root: classes.pagination }}
                                count={Math.ceil(listCount / 10)}
                                page={page}
                                onChange={handlePageChange}
                                showFirstButton
                                showLastButton
                            />
                        </Stack>
                    </TableContainer>
                )}
            </Grid>
        </div>
    );
};

export default HoldJobsDetails;
